import { Autocomplete } from "@material-ui/lab";
import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import HttpClient from "../../lib/HttpClient";
import { useSnackbarContext } from "../../contexts/Snackbar";

interface ReferedByDoctorModalProps {
  show: boolean;
  onHide: () => void;
  appointmentId: string;
}

const validatePhoneNumber = (phone: string) => {
  if (!phone || phone.trim() === "") return true; // optional field

  const cleaned = phone.replace(/\D/g, ""); // remove non-digits

  return (
    cleaned.length === 10 || (cleaned.length === 12 && cleaned.startsWith("91"))
  );
};

const ReferedByDoctorModal: React.FC<ReferedByDoctorModalProps> = ({
  show,
  onHide,
  appointmentId,
}) => {
  const { setSnackbar } = useSnackbarContext();
  const [referedDoctorName, setReferedDoctorName] = useState<string>("");
  const [referedDoctorPhone, setReferedDoctorPhone] = useState<string>("");

  const [loading, setLoading] = useState<boolean>(true);
  const [doctorNameSuggestions, setDoctorNameSuggestions] = useState<
    { name: string; refDoctorId: string }[]
  >([]);

  const requestGetPatientsReferedDoctor = async () => {
    const URL = `/api/v1/users/referral/${appointmentId}`;

    setLoading(true);
    try {
      const response = await HttpClient.performGet<any>(URL);
      if (!response?.body?.status?.success) {
        throw new Error("Failed to fetch refered doctor");
      }

      response?.body?.referral?.name &&
        setReferedDoctorName(response?.body?.referral?.name);
      response?.body?.referral?.mobile &&
        setReferedDoctorPhone(response?.body?.referral?.mobile);
      setDoctorNameSuggestions(response?.body?.referralOptions);
    } catch (err) {
      console.error("Failed to fetch refered doctor", err);
    } finally {
      setLoading(false);
    }
  };

  const requestPostPatientsReferedDoctor = async () => {
    const URL = `/api/v1/users/referral/${appointmentId}`;

    if (referedDoctorName.trim().length <= 3) {
      setSnackbar({
        message: "Doctor's name must be more than 3 characters.",
        severity: "error",
      });
      return;
    }
    const trimmedPhone = referedDoctorPhone.trim();

    if (trimmedPhone && !validatePhoneNumber(trimmedPhone)) {
      setSnackbar({
        message: "Please enter a valid 10-digit phone number.",
        severity: "error",
      });
      return;
    }

    try {
      const response = await HttpClient.performPost<any>(URL, {
        name: referedDoctorName,
        mobile: referedDoctorPhone,
      });
      if (!response?.body?.status?.success) {
        throw new Error("Failed to fetch refered doctor");
      }

      onHide();
    } catch (err) {
      setSnackbar({
        message: "Something went wrong. " + (err as Error).message,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    requestGetPatientsReferedDoctor();
  }, []);

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      backdrop="static"
      scrollable
    >
      <Modal.Header closeButton>
        <Modal.Title>Referred By </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ minHeight: "100px" }}>
        {loading ? (
          <div className="d-flex justify-content-center align-items-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <div className="container-fluid px-0">
            {/* Doctor Name */}
            <div className="row align-items-center mb-3">
              <div className="col-md-2">
                <label className="fw-bold mb-0">Doctor Name:</label>
              </div>
              <div className="col-md-10">
                <Autocomplete
                  freeSolo
                  disablePortal={false}
                  inputValue={referedDoctorName}
                  value={referedDoctorName}
                  onInputChange={(event, newInputValue) => {
                    const capitalizedValue = newInputValue
                      .split(" ")
                      .map(
                        (word) =>
                          word.charAt(0).toUpperCase() +
                          word.slice(1).toLowerCase(),
                      )
                      .join(" ");

                    setReferedDoctorName(capitalizedValue);
                  }}
                  onChange={(event, newValue) => {
                    setReferedDoctorName(newValue || "");
                  }}
                  options={(doctorNameSuggestions || [])
                    .map((opt) => opt.name)
                    .filter((name) =>
                      name
                        .toLowerCase()
                        .includes(referedDoctorName?.toLowerCase()),
                    )}
                  renderInput={(params) => (
                    <div ref={params.InputProps.ref}>
                      <input
                        type="text"
                        {...params.inputProps}
                        className="form-control"
                        placeholder="Enter Referred Doctor's Name"
                      />
                    </div>
                  )}
                />
              </div>
            </div>

            {/* Doctor Phone */}
            <div className="row align-items-center">
              <div className="col-md-2">
                <label className="fw-bold mb-0">Doctor Phone:</label>
              </div>
              <div className="col-md-10">
                <input
                  type="number"
                  inputMode="numeric"
                  className="form-control"
                  placeholder="Enter Referred Doctor's Phone"
                  value={referedDoctorPhone}
                  onChange={(e) => setReferedDoctorPhone(e.target.value)}
                />
              </div>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-end align-items-end">
          <button className="btn btn_outline my-2" onClick={onHide}>
            Cancel
          </button>
          <button
            className="btn btn_primary m-2 "
            onClick={requestPostPatientsReferedDoctor}
            disabled={loading}
          >
            Save
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ReferedByDoctorModal;
